import { ReactNode } from "react";
import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import { useTranslation } from "next-i18next";

import Button from "./Button";

const ConfirmationDialog = ({
  isOpen,
  onConfirm,
  onCancel,
  title,
  message,
  confirmText,
  cancelText,
  children,
  confirmDisabled = false,
  loading = false,
}: {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  children?: ReactNode;
  confirmDisabled?: boolean;
  loading?: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <Dialog open={isOpen} onClose={() => {}} className="relative z-50">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="w-full max-w-md transform rounded-lg bg-white p-6 shadow-xl md:p-8">
          {/* Header Section */}
          <div className="mb-6 flex items-start justify-between">
            <DialogTitle className="text-xl font-semibold leading-6 text-gray-900">
              {title || t("notifications.confirmUnsubscribe")}
            </DialogTitle>
          </div>

          {/* Warning Icon and Message */}
          <div className="mb-8">
            <div className="mb-6 flex justify-center">
              <div className="rounded-full bg-red-100 p-3">
                <svg
                  className="h-6 w-6 text-red-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
                  />
                </svg>
              </div>
            </div>
            <div className="space-y-4">
              <p className="text-center text-sm text-gray-500">
                {message || t("notifications.confirmUnsubscribeMessage")}
              </p>
              {children}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
            <Button className="w-full sm:w-auto" variant="outline" onClick={onCancel} disabled={loading}>
              {cancelText || t("common.cancel")}
            </Button>
            <Button
              className="w-full sm:w-auto"
              variant="danger"
              onClick={onConfirm}
              disabled={confirmDisabled}
              loading={loading}
            >
              {confirmText || t("notifications.unsubscribeConfirm")}
            </Button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
};

export default ConfirmationDialog;
